// TRAIL LINES plan (ways-3d-coloured-lines; pure: no THREE, no DOM —
// scripts/check-lines.mjs and scripts/check-focus.mjs run it in bare node).
// client/src/traillines.js draws it. The trail lines are a zoom INSTRUMENT
// layer (owner 2026-10-11): the ways of the trail index drawn as coloured
// lines in the 3D view, only on the focus layer (the armed desktop / phone
// view, the optic band, a raised VR optic), fading in with the view zoom and
// OFF at 1x. The world itself stays real (APP_PLAYER "Trail marks": abstract
// cues only on the map and the HUD — the instrument is the HUD's twin).
//
//   colour   lineLooks: the 2D Map's coarse trail buckets (maplayers.js
//            trailBuckets(entries, false)) — the ONE source; a colour here is
//            the bucket's `col` x its alpha, never a second table
//   fade     lineFade: 0 at and below view_arm.zoom_on (so 0 at 1x), linear
//            in ln Z to 1 at band entry (fov0 / fov_deg), where the HUD's w
//            reaches 1
//   pieces   linePieces: a trail's SURFACE stretches (station kind 0 — the
//            RoadGraph's rule; a deck / bore stretch breaks the line, the
//            ground there is not the way), Douglas-Peucker on xz at `tol`,
//            every segment cut to <= `step` so the drape follows the drawn
//            facets
//   chunks   lineChunksJob: per chunk_m chunk one polyline buffer (points
//            x y z, a segment = point k -> k + 1, a piece break = a segment
//            with alpha 0), as a step generator (sched.js slices)
//   height   drapePoints: y = the drawn ground the optic sees
//            (terrain.groundDrawn(x, z) — the structure FAR_RULE's source,
//            the ground tree's finest drawn node evaluated as its vertex
//            shader draws it); no drawn node -> LINE_HIDE_Y (not
//            drawn). linePull: the view-ray pull the shader applies (the line
//            moves TOWARD the camera along its own ray: the same pixel, a
//            nearer depth), so a chord under a convex drawn facet does not
//            bury the line and nothing floats. lineSegShown: a segment
//            steeper than grade_max is a chord across a drawn step (a ring /
//            focus tile edge over the lower envelope), not drawn
//   plan     linePlan: farplan.js farPlan itself (the far-building decision:
//            the chunks the wedge reaches to the frame edge, nearest the apex
//            first, under focus.tiers[t].trail_lines {tri_budget, draw_budget})
import { trailBuckets } from './maplayers.js';
import { farPlan } from './farplan.js';

export const LINE_HIDE_Y = -1e5;     // a point with no drawn ground (or not draped yet): its segments are culled
export const LINE_TRIS = 2;          // one screen-space quad per segment (traillines.js)

// css colour ('#rrggbb' | 'rgb(a)(r, g, b[, a])') -> [r, g, b, a] (bytes, a 0..1)
export function parseCss(c) {
  const s = String(c).trim();
  let m = /^#([0-9a-f]{6})$/i.exec(s);
  if (m) {
    const v = parseInt(m[1], 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255, 1];
  }
  m = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(s);
  if (m) return [+m[1], +m[2], +m[3], m[4] === undefined ? 1 : +m[4]];
  throw new Error(`lineplan: unparsed colour ${c}`);
}

// trail entries -> Map(entry -> {rgb, a, w, key}): the Map's coarse trail
// buckets, exactly (an entry the Map does not stroke — no pts — has no look
// and no line)
export function lineLooks(entries) {
  const out = new Map();
  for (const b of trailBuckets(entries, false)) {
    const [r, g, bl, a] = parseCss(b.look.col);
    const look = { rgb: [r, g, bl], a: a * b.look.alpha, w: b.look.w, key: b.look.key };
    for (const e of b.items) out.set(e, look);
  }
  return out;
}

// the instrument's fade at view zoom z: 0 at / below zOn, 1 from zFull on
export function lineFade(z, zOn, zFull) {
  if (!(z > zOn)) return 0;
  if (!(zFull > zOn)) return 1;
  return Math.min(1, Math.log(z / zOn) / Math.log(zFull / zOn));
}

// the view-ray pull (m) at view distance d (m)
export const linePull = (d, cfg) => cfg.pull_m + cfg.pull_k * d;

// is a draped segment drawn? Not when an end has no drawn ground, nor when it
// climbs steeper than grade_max (+ grade_pad_m): that is a chord across a
// drawn STEP — a finer ring's or focus tile's edge over the coarser lower
// envelope (tens of metres at the L3 / L4 edge) — not a way. The vertex
// shader (traillines.js) applies the same test.
export function lineSegShown(ax, ay, az, bx, by, bz, cfg) {
  if (ay === LINE_HIDE_Y || by === LINE_HIDE_Y) return false;
  return Math.abs(by - ay) <= cfg.grade_max * Math.hypot(bx - ax, bz - az) + cfg.grade_pad_m;
}

// Douglas-Peucker on xz over stations [a, b] (inclusive): kept indices
function dp(X, Z, a0, b0, tol, out) {
  const t2 = tol * tol;
  const keep = new Uint8Array(b0 - a0 + 1);
  keep[0] = keep[b0 - a0] = 1;
  const st = [a0, b0];
  while (st.length) {
    const b = st.pop(), a = st.pop();
    if (b - a < 2) continue;
    const ax = X[a], az = Z[a], dx = X[b] - ax, dz = Z[b] - az, L2 = dx * dx + dz * dz;
    let best = -1, bd = t2;
    for (let i = a + 1; i < b; i++) {
      const px = X[i] - ax, pz = Z[i] - az;
      let d2;
      if (L2 < 1e-9) d2 = px * px + pz * pz;
      else {
        const u = (px * dx + pz * dz) / L2;
        if (u < 0) d2 = px * px + pz * pz;
        else if (u > 1) { const qx = X[i] - X[b], qz = Z[i] - Z[b]; d2 = qx * qx + qz * qz; }
        else { const c = px * dz - pz * dx; d2 = (c * c) / L2; }
      }
      if (d2 > bd) { bd = d2; best = i; }
    }
    if (best > 0) { keep[best - a0] = 1; st.push(a, best, best, b); }
  }
  for (let i = 0; i < keep.length; i++) if (keep[i]) out.push(a0 + i);
}

// one trail's drawable polylines -> [Float64Array xz interleaved, …].
// src: the run's stations {x, z, kind} (trail index `st`, ways.json order;
// buildTrailIndex keeps them for every run — a run without them throws).
export function linePieces(src, tol, step) {
  const out = [];
  if (!src || !src.x || !src.z || !src.kind || src.kind.length !== src.x.length) {
    throw new Error('trail line source without stations {x, z, kind}: buildTrailIndex keeps them for every run');
  }
  const X = src.x, Z = src.z, K = src.kind, n = X.length;
  let i = 0;
  while (i < n) {
    if (K[i] !== 0) { i++; continue; }
    let j = i;
    while (j + 1 < n && K[j + 1] === 0) j++;
    if (j > i) {
      const keep = [];
      dp(X, Z, i, j, tol, keep);
      const P = [X[keep[0]], Z[keep[0]]];
      for (let k = 1; k < keep.length; k++) {
        const ax = X[keep[k - 1]], az = Z[keep[k - 1]], bx = X[keep[k]], bz = Z[keep[k]];
        const m = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / step));
        for (let s = 1; s <= m; s++) P.push(ax + (bx - ax) * s / m, az + (bz - az) * s / m);
      }
      out.push(Float64Array.from(P));
    }
    i = j + 1;
  }
  return out;
}

// The chunked line buffers, as a step generator (yields after the looks,
// every `every` stations simplified and every chunk packed; traillines.js
// runs it over sched.js slices — check-lines.mjs times the longest step). entries: the trail index;
// srcOf(entry) -> its stations (buildTrailIndex `st`); cfg: renderconfig
// trail_lines {tol_m, step_m, chunk_m}. Result (out): {chunks: [{cx, cz,
// chunkM, pts Float32Array(3n) (y = LINE_HIDE_Y until draped), col
// Uint8Array(4(n-1)) per segment (sRGB bytes + alpha byte, 0 = a piece
// break), wid Uint8Array(n-1) (the Map's stroke width x 10), segs (drawn
// segments), ntris, subs (farPlan's one run), x0 z0 x1 z1}], stats}.
export function* lineChunksJob(entries, srcOf, cfg, out, every = 1024) {
  const looks = lineLooks(entries);
  yield;
  const M = cfg.chunk_m, by = new Map();
  const stats = { runs: 0, pieces: 0, points: 0, segs: 0, breaks: 0, chunks: 0, tris: 0 };
  const chunkOf = (x, z) => {
    const cx = Math.floor(x / M), cz = Math.floor(z / M), k = cx + '_' + cz;
    let c = by.get(k);
    if (!c) by.set(k, c = { cx, cz, P: [], C: [], W: [] });
    return c;
  };
  // a piece's points [s, e] into chunk c (a break first when c holds points)
  const put = (c, P, s, e, rgba, w) => {
    if (c.P.length) { c.C.push(0, 0, 0, 0); c.W.push(0); stats.breaks++; }
    for (let k = s; k <= e; k++) {
      c.P.push(P[2 * k], P[2 * k + 1]);
      if (k > s) { c.C.push(rgba[0], rgba[1], rgba[2], rgba[3]); c.W.push(w); stats.segs++; }
    }
  };
  let work = 0;
  for (const e of entries) {
    const look = looks.get(e);
    if (look) {
      stats.runs++;
      const src = srcOf(e);
      work += src ? src.x.length : 0;
      const rgba = [look.rgb[0], look.rgb[1], look.rgb[2], Math.max(1, Math.round(look.a * 255))];
      const w = Math.max(1, Math.min(255, Math.round(look.w * 10)));
      for (const P of linePieces(src, cfg.tol_m, cfg.step_m)) {
        stats.pieces++;
        stats.points += P.length >> 1;
        // split where a point leaves the chunk: the crossing segment stays
        // with the chunk it starts in, the next chunk starts at its end
        const n = P.length >> 1;
        let s = 0, c = chunkOf(P[0], P[1]);
        for (let k = 1; k < n; k++) {
          const ck = chunkOf(P[2 * k], P[2 * k + 1]);
          if (ck === c) continue;
          put(c, P, s, k, rgba, w);
          s = k; c = ck;
        }
        if (n - 1 > s) put(c, P, s, n - 1, rgba, w);
      }
    }
    if (work >= every) { work = 0; yield; }
  }
  const chunks = [];
  for (const c of [...by.values()].sort((a, b) => a.cz - b.cz || a.cx - b.cx)) {
    const n = c.P.length >> 1;
    if (n < 2) continue;
    const pts = new Float32Array(3 * n);
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let k = 0; k < n; k++) {
      const x = c.P[2 * k], z = c.P[2 * k + 1];
      pts[3 * k] = x; pts[3 * k + 1] = LINE_HIDE_Y; pts[3 * k + 2] = z;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (z < z0) z0 = z; if (z > z1) z1 = z;
    }
    const ntris = LINE_TRIS * (n - 1);
    let segs = 0;
    for (let s = 3; s < c.C.length; s += 4) if (c.C[s]) segs++;
    chunks.push({ cx: c.cx, cz: c.cz, chunkM: M, pts, col: Uint8Array.from(c.C), wid: Uint8Array.from(c.W), segs, ntris,
      subs: [{ key: c.cx + '_' + c.cz, tri0: 0, ntris }], x0, z0, x1, z1 });
    stats.tris += ntris;
    yield;
  }
  stats.chunks = chunks.length;
  out.chunks = chunks;
  out.stats = stats;
}

// the job run to its end in one go (gates, tests)
export function lineChunks(entries, srcOf, cfg) {
  const out = {};
  for (const _ of lineChunksJob(entries, srcOf, cfg, out)) { /* run to the end */ }
  return out;
}

// THE decision: the far-building plan itself (farplan.js), one run per chunk:
// the chunks the wedge reaches (to the frame edge), nearest the apex first,
// admitted under cfg {tri_budget, draw_budget}; off without an active wedge.
export const linePlan = (chunks, focus, cfg) => farPlan(chunks, focus, null, cfg);

// The drape: y = G(x, z) for points [i0, i0 + n) of one chunk's buffer
// (resumable; G = (x, z) -> the drawn ground the optic sees, NaN where no tile
// is resident -> LINE_HIDE_Y). -> {next, changed, ylo, yhi} (ylo / yhi over
// the draped points of this call; Infinity / -Infinity when none).
export function drapePoints(pts, G, i0, n, eps = 0.02) {
  const N = pts.length / 3, end = Math.min(N, i0 + n);
  let changed = false, ylo = Infinity, yhi = -Infinity;
  for (let k = i0; k < end; k++) {
    const g = G(pts[3 * k], pts[3 * k + 2]);
    const y = g === g ? g : LINE_HIDE_Y;
    if (Math.abs(y - pts[3 * k + 1]) > eps) { pts[3 * k + 1] = y; changed = true; }
    if (y !== LINE_HIDE_Y) { if (y < ylo) ylo = y; if (y > yhi) yhi = y; }
  }
  return { next: end, changed, ylo, yhi };
}
